import { type HttpClientInterface, type HttpClientRequestConfig } from 'core/infra/http/HttpClient'

import { type Shipper, type ShipperDetail } from '../domain/shippers'
import {
  type RawShipperDetail,
  type RawShippersResponse,
  mapApiToShipperDetail,
  mapApiToShippers,
} from './ShipperMapper'

export type ShipperRepositoryDependencies = {
  httpClient: HttpClientInterface
}

export type GetShippersParams = {
  limit?: number
  offset?: number
  search?: string
}

export type GetShippersResult = {
  shippers: Shipper[]
  totalCount: number
}

export class ShipperRepository {
  private httpClient: HttpClientInterface

  constructor({ httpClient }: ShipperRepositoryDependencies) {
    this.httpClient = httpClient
  }

  async getShippers(
    params: GetShippersParams = {},
    config?: Partial<HttpClientRequestConfig>
  ): Promise<GetShippersResult> {
    const { limit = 10, offset = 0, search } = params
    const endpoint = `/api/v2/shipperguide/carriers/shippers`

    const response = await this.httpClient.get<RawShippersResponse>(endpoint, {
      ...config,
      params: {
        limit,
        offset,
        ...(search && { search }),
      },
    })

    try {
      return mapApiToShippers(response.data)
    } catch (error) {
      throw new Error('Failed to map API return to Shippers', { cause: error as Error })
    }
  }

  async getShipperByUUID(
    uuid: string,
    config?: Partial<HttpClientRequestConfig>
  ): Promise<ShipperDetail> {
    const endpoint = `/api/v2/shipperguide/carriers/shippers/${uuid}`

    const response = await this.httpClient.get<RawShipperDetail>(endpoint, config)

    try {
      return mapApiToShipperDetail(response.data)
    } catch (error) {
      throw new Error('Failed to map API return to ShipperDetail', { cause: error as Error })
    }
  }
}
